import { type CSSProperties, type JSX, useState } from 'react'

import { AnimatedBadge, IconAnchorLink } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import { getAlignmentClass } from '~/portal/components/SyntaxRenderer/components/helpers'
import appDefaults from '~/portal/portal.defaults.json'
import { useNotification } from '~/shared/hooks/useNotification.ts'
import { CONTENT_TYPE } from '~/shared/types/content'

export function Heading ({
  children,
  level,
  id,
  contentType,
  style,
}: {
  children: string | JSX.Element | JSX.Element[],
  level: number,
  id: string,
  contentType?: CONTENT_TYPE,
  style?: CSSProperties,
}) {
  const location = useLocation()

  const navigate = useNavigate()

  const { flashSuccessMobile } = useNotification()

  const { t } = useTranslation()

  const isMobile = useMediaQuery(appDefaults.media.mobile, { initializeWithValue: false })

  const [copied, setCopied] = useState(false)

  const [visible, setVisible] = useState(false)

  const Tag = `h${level}` as keyof JSX.IntrinsicElements

  const alignment = getAlignmentClass(style)

  const copyLink = async () => {
    const encodedId = encodeURIComponent(id)

    if (window.navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}${location.pathname}${location.search}#${encodedId}`)
    }

    navigate(`${location.search}#${encodedId}`)

    setCopied(true)
    setVisible(true)
    
    if (isMobile) {
      flashSuccessMobile({
        message: t('consumer.product.anchorLink.flashSuccessMobile'),
      }) 
    }
    setTimeout(() => {
      setVisible(false)
      setTimeout(() => setCopied(false), 400)
    }, 3000)
  }

  return (
    <Tag id={id} className={alignment?.text}>
      {(level === 2 || level === 3) && id ? 
        <div
          className={clsx(
            'flex items-center group',
            alignment?.justify,
            contentType === CONTENT_TYPE.A2A && level === 3 &&
            '[&_code]:ml-1.5 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[#c7254e] [&_code]:bg-[#f9f2f4] dark:[&_code]:text-[#ffd1dc] dark:[&_code]:bg-[#6d2d44]'
          )}
        >
          {children}
          {copied && !isMobile ? 
            <div className={clsx(copied ? 'visible' : 'invisible group-hover:visible')}>
              <AnimatedBadge text={t('consumer.product.anchorLink.copiedBadge')} visible={visible} />
            </div>
            : 
            <div
              className={clsx(
                'flex items-center justify-center w-5 h-5 ml-1.5 cursor-pointer',
                !isMobile ? 'invisible group-hover:visible' : 'visible'
              )}
              onClick={copyLink}
            >
              <IconAnchorLink />
            </div>}
        </div>
        : children}
    </Tag>
  )
}
